import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlayCircle, Video as VideoIcon } from "lucide-react";
import TrainingVideoManager from "@/components/TrainingVideoManager";
import { useAuth } from "@/context/AuthContext";

// How-to video viewer for Print Studio. Reuses the training_videos store (category "studio").
// refId null => global (all studio videos); refId set => videos tagged to that tab.
export default function StudioVideos({ refId = null, refLabel = "Print Studio", label = "Guides", variant = "outline" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const es = (localStorage.getItem("pns_train_lang") || "es") !== "en";

  const load = () => api.get("/training/videos", { params: { category: "studio", ref_id: refId || undefined } })
    .then(({ data }) => setVideos(data || [])).catch(() => {});
  useEffect(() => { load(); }, [refId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = (v) => (es ? v.title_es : v.title_en) || v.title_en || v.title_es || v.url;

  const isGlobal = !refId;
  const btnTip = isGlobal
    ? "Guides: shows ALL Print Studio how-to videos together, from every tab. Add a video here to make it global."
    : `Videos: shows only the how-to videos for the "${refLabel}" tab. Add a video here to tag it to this tab (it also appears under Guides).`;

  return (
    <>
      <Button size="sm" variant={variant} title={btnTip} onClick={() => setOpen(true)} data-testid={`studio-videos-btn-${refId || "all"}`}>
        <PlayCircle size={15} className="mr-1.5" /> {label}{videos.length ? ` (${videos.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="studio-videos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>How-to videos — {refLabel}</span>
              {isAdmin && <TrainingVideoManager category="studio" refId={refId || undefined} refLabel={refLabel} variant="button" />}
            </DialogTitle>
          </DialogHeader>
          {isAdmin && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500 leading-relaxed" data-testid="studio-videos-help">
              <p className="font-semibold text-slate-600 mb-1">How to add a video</p>
              <p>Click the <b>“Videos”</b> button above, press <b>Add video</b>, paste a YouTube / Vimeo / Drive / OneDrive link, add a title and Save.</p>
              <p className="mt-1.5">
                {isGlobal
                  ? "You are in Guides → the video will be saved as GLOBAL and shown across the whole Print Studio."
                  : `You are in the "${refLabel}" tab → the video will be tagged to THIS tab (and will also appear under Guides).`}
              </p>
            </div>
          )}
          {videos.length === 0 ? (
            <div className="text-center text-slate-400 py-12 text-sm" data-testid="studio-videos-empty">
              <VideoIcon size={34} className="mx-auto mb-2 opacity-50" />
              No videos yet.{isAdmin ? " Use the “Videos” button above to paste a YouTube link." : ""}
            </div>
          ) : (
            <div className="space-y-5">
              {videos.map((v) => (
                <div key={v.id} data-testid={`studio-video-${v.id}`}>
                  <div className="text-sm font-medium text-slate-700 mb-1.5">{title(v)}</div>
                  {v.embed_url ? (
                    <div className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe src={v.embed_url} title={title(v)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                  ) : (
                    <a href={v.url} target="_blank" rel="noreferrer" className="text-[#2495D3] text-sm">Open video ↗</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
